import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListResponse, ListSearch, ModuleV1Read, SortOption } from '@ca/core/models';
import { ModulesAdminV1Service, ModulesWorkspaceV1Service, UserNotificationService } from '@ca/core/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { isEqual } from 'src/utils/utils';
import * as myModulesActions from './actions/_my-modules/my-modules.actions';
import * as appRootActions from './actions/app-root/app-root.actions';
import * as moduleDetailsPageActions from './actions/module-details-page/module-details-page.actions';
import * as myModulesPageActions from './actions/my-modules-page/my-modules-page.actions';
import * as fromMyModules from './my-modules.reducer';
import { selectMyModulesState } from './my-modules.selectors';

@Injectable()
export class MyModulesEffects {
    loadMyModules$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(myModulesActions.loadMyModules, myModulesPageActions.loadMyModules, moduleDetailsPageActions.loadMyModules, appRootActions.loadMyModules),
            concatMap(action => of(action).pipe(withLatestFrom(this.store$.select(selectMyModulesState)))),
            mergeMap(([action, state]) => {
                const params = this.buildHttpParams(action.query);
                const query = this.getListSearchOrDefaultListSearch(action.query);
                if (this.isActionQueryAndStateQueryEqual(state.query, query) && !action.isReloadEnforced) {
                    return of(
                        myModulesActions.loadMyModulesCacheSuccess({
                            modules: state.modules,
                            filteredTotalCount: state.filteredTotalCount,
                            query: state.query,
                        }),
                    );
                }

                return this.modulesWorkspaceV1Service.getWorkspaceModules(params).pipe(
                    map((data: ListResponse<ModuleV1Read>) => {
                        const modules = data.data;
                        const count = data?.meta?.totalRecordCount ?? 0;
                        return myModulesActions.loadMyModulesSuccess({ modules, filteredTotalCount: count, query });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        const errorMessage = error.message ? error.message : JSON.stringify(error);
                        this.userNotificationService.showError(errorMessage);
                        return of(myModulesActions.loadMyModulesFailure({ error: { loading: error } }));
                    }),
                );
            }),
        );
    });

    loadMyModulesTotalCount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(appRootActions.loadMyModulesTotalCount, myModulesActions.loadMyModulesTotalCount, myModulesPageActions.loadMyModulesTotalCount),
            concatMap(() => {
                const query = this.getListSearchOrDefaultListSearch();
                const params = this.buildHttpParams(query);
                return this.modulesWorkspaceV1Service.getWorkspaceModules(params).pipe(
                    map((data: ListResponse<ModuleV1Read>) => {
                        const totalCount = data?.meta?.totalRecordCount ?? 0;
                        return myModulesActions.loadMyModulesTotalCountSuccess({ totalCount });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        const errorMessage = error.message ? error.message : JSON.stringify(error);
                        this.userNotificationService.showError(errorMessage);
                        return of(myModulesActions.loadMyModulesTotalCountFailure({ error: { loadingTotalCount: error } }));
                    }),
                );
            }),
        );
    });

    deleteMyModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(moduleDetailsPageActions.deleteMyModule),
            concatMap(action => {
                return this.modulesWorkspaceV1Service.delete(action.module.id).pipe(
                    mergeMap(() => {
                        return [
                            myModulesActions.deleteMyModuleSuccess(),
                            myModulesActions.loadMyModules({ isReloadEnforced: true }),
                            myModulesActions.loadMyModulesTotalCount(),
                        ];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        const errorMessage = error.message ? error.message : JSON.stringify(error);
                        this.userNotificationService.showError(errorMessage);
                        return of(myModulesActions.deleteMyModuleFailure({ error: { delete: error } }));
                    }),
                );
            }),
        );
    });

    updateMyModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(moduleDetailsPageActions.updateMyModule),
            concatMap(action => {
                return this.modulesWorkspaceV1Service.updateModule(action.moduleUpdateModel).pipe(
                    mergeMap((module: ModuleV1Read) => {
                        this.userNotificationService.showSuccess('MODULES.MY_MODULES.UPDATE_SUCCESS');
                        return [myModulesActions.updateMyModuleSuccess({ module }), myModulesActions.loadMyModules({ isReloadEnforced: true })];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        let updateErrorMessage = this.translateService.instant('MODULES.EDITOR.FAILED_TO_SAVE_YOUR_CHANGES');
                        if (error.error) {
                            updateErrorMessage = `${updateErrorMessage}: ${error.error}`;
                        } else {
                            updateErrorMessage = `${updateErrorMessage}.`;
                        }
                        this.userNotificationService.showError(updateErrorMessage);
                        console.error(`${updateErrorMessage}`, error);
                        return of(myModulesActions.updateMyModuleFailure({ error: { update: error } }));
                    }),
                );
            }),
        );
    });

    updateMyModuleLatestPublicVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(moduleDetailsPageActions.updateLatestPublicVersion),
            concatMap(action => {
                return this.adminModuleService.updateLatestPublicVersion(action.moduleUpdateModel.id, action.moduleUpdateModel.moduleUpdates).pipe(
                    mergeMap((module: ModuleV1Read) => {
                        this.userNotificationService.showSuccess('MODULES.MY_MODULES.UPDATE_SUCCESS');
                        return [myModulesActions.updateMyModuleSuccess({ module }), myModulesActions.loadMyModules({ isReloadEnforced: true })];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        let updateErrorMessage = this.translateService.instant('MODULES.EDITOR.FAILED_TO_SAVE_YOUR_CHANGES');
                        if (error.error) {
                            updateErrorMessage = `${updateErrorMessage}: ${error.error}`;
                        } else {
                            updateErrorMessage = `${updateErrorMessage}.`;
                        }
                        this.userNotificationService.showError(updateErrorMessage);
                        console.error(`${updateErrorMessage}`, error);
                        return of(myModulesActions.updateMyModuleFailure({ error: { update: error } }));
                    }),
                );
            }),
        );
    });

    updateAndSubmitModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(moduleDetailsPageActions.updateAndSubmitModule),
            concatMap(action => {
                return this.modulesWorkspaceV1Service.updateModule(action.data.moduleUpdateData).pipe(
                    switchMap(() => {
                        this.userNotificationService.showSuccess('MODULES.MY_MODULES.UPDATE_SUCCESS');
                        return this.modulesWorkspaceV1Service.publishModule(action.data.submissionData);
                    }),
                    mergeMap((publishedModule: ModuleV1Read) => {
                        this.userNotificationService.showSuccess('MODULES.MY_MODULES.UPDATE_AND_PUBLISH');
                        return [
                            myModulesActions.publishMyModuleSuccess({ module: publishedModule }),
                            myModulesActions.loadMyModules({ isReloadEnforced: true }),
                        ];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error?.url?.includes('submissions')) {
                            let publishErrorMessage = this.translateService.instant('MODULES.PUBLISH.FAILED_TO_SUBMIT_YOUR_MODULE');
                            if (error.error) {
                                publishErrorMessage = `${publishErrorMessage}: ${error.error}`;
                            } else {
                                publishErrorMessage = `${publishErrorMessage}.`;
                            }
                            this.userNotificationService.showError(publishErrorMessage);
                            console.error(`${publishErrorMessage}`, error);
                            return of(myModulesActions.publishMyModuleFailure({ error: { publish: error } }));
                        }
                        let updateErrorMessage = this.translateService.instant('MODULES.EDITOR.FAILED_TO_SAVE_YOUR_CHANGES');
                        if (error.error) {
                            updateErrorMessage = `${updateErrorMessage}: ${error.error}`;
                        } else {
                            updateErrorMessage = `${updateErrorMessage}.`;
                        }
                        this.userNotificationService.showError(updateErrorMessage);
                        console.error(`${updateErrorMessage}`, error);
                        return of(myModulesActions.updateMyModuleFailure({ error: { update: error } }));
                    }),
                );
            }),
        );
    });

    constructor(
        private actions$: Actions,
        private adminModuleService: ModulesAdminV1Service,
        private modulesWorkspaceV1Service: ModulesWorkspaceV1Service,
        private store$: Store<fromMyModules.MyModulesState>,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
    ) {}

    private getListSearchOrDefaultListSearch(query?: ListSearch): ListSearch {
        return {
            categoryIds: query?.categoryIds || '',
            complianceIds: query?.complianceIds || '',
            isOpenSource: query?.isOpenSource || undefined,
            pageNumber: query?.pageNumber || 1,
            pageSize: query?.pageSize || 11,
            searchQuery: query?.searchQuery || '',
            sortBy: <SortOption>(query?.sortBy || '-sortOrderDate'),
        };
    }

    private isActionQueryAndStateQueryEqual(stateQuery?: ListSearch, actionQuery?: ListSearch): boolean {
        if (!!stateQuery && isEqual(stateQuery, actionQuery)) {
            return true;
        }
        return false;
    }

    private buildHttpParams(query?: ListSearch): HttpParams {
        const newQuery = this.getListSearchOrDefaultListSearch(query);

        let params = new HttpParams()
            .set('page[number]', newQuery.pageNumber.toString())
            .set('page[size]', newQuery.pageSize.toString())
            .set('sort', newQuery.sortBy)
            .set('search', newQuery.searchQuery);

        if (newQuery.categoryIds) {
            params = params.set('categoryIds', newQuery.categoryIds);
        }

        if (newQuery.complianceIds) {
            params = params.set('complianceIds', newQuery.complianceIds);
        }

        if (newQuery.isOpenSource) {
            params = params.set('isopensource', newQuery.isOpenSource.toString());
        }

        return params;
    }
}
